import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Box, Grid } from "@mui/material";
import BackdropScreen from "bundles/common/components/BackdropScreen";
import trackEvent from "bundles/common/utils/trackEvent";
import { FormattedMessage, useIntl } from "react-intl";
import useMediaQuery from "@mui/material/useMediaQuery";
import sh from "bundles/common/utils/sh";

import HeaderRowKanban from "./CandidatesContent/components/HeaderRowKanban";
import StatsCardKanban from "./CandidatesContent/components/StatsCardKanban";
import KanbanBoard from "./CandidatesContent/components/KanbanBoard";
import ClosedCandidatesSection from "./CandidatesContent/components/ClosedCandidatesSection";

const normalizeCardsByColumn = (columns, cardsByColumn) => {
  const next = { ...(cardsByColumn || {}) };
  (columns || []).forEach((c) => {
    const k = String(c.id);
    if (!Array.isArray(next[k])) next[k] = [];
  });
  return next;
};

const sortColumnsByPosition = (cols) => {
  return [...(cols || [])].sort((a, b) => (a.position || 0) - (b.position || 0));
};

const CandidatesContent = ({ kanban, setKanban, kanbanColumns, loading, onMetricsInvalidate }) => {
  const isDesktop = useMediaQuery((theme) => theme.breakpoints.up("md"));
  const intl = useIntl();
  const [query, setQuery] = useState("");
  const [loadingAllCards, setLoadingAllCards] = useState(false);
  const [allCardsLoaded, setAllCardsLoaded] = useState(false);

  useEffect(() => {
    trackEvent("kanban", "board_viewed");
  }, []);

  const stats = kanban?.stats || {};
  const totalCandidatesInKanban = stats.total_active_cards ?? 0;
  const newApplicantApplicationsThisWeek = stats.new_this_week ?? 0;
  const conversionRateRecruited = stats.conversion_rate_recruited;
  const medianTimeInPendingDays = stats.median_time_in_pending_days;
  const waitingOver7Days = stats.waiting_over_7_days ?? 0;
  const lastUpdatedAt = stats.last_updated_at;
  const recruitmentsCurrentMonth = stats.recruitments_current_month ?? 0;
  const closuresCurrentMonth = stats.closures_current_month ?? 0;
  const avgTimePendingToHiredDays = stats.avg_time_pending_to_hired_days;
  const columns = useMemo(() => {
    const nextColumns = kanban?.columns || kanbanColumns || [];
    return sortColumnsByPosition(nextColumns);
  }, [kanban?.columns, kanbanColumns]);
  const stepsCount = columns.length;

  const cardsByColumnId = useMemo(() => {
    return normalizeCardsByColumn(columns, kanban?.cards_by_column_id || {});
  }, [columns, kanban?.cards_by_column_id]);

  const columnCountsById = kanban?.column_counts_by_id || {};

  const setColumns = (updater) => {
    if (typeof setKanban !== "function") return;
    setKanban((prev) => {
      const prevCols = prev?.columns || kanbanColumns || [];
      const next = typeof updater === "function" ? updater(prevCols) : updater;
      return { ...(prev || {}), columns: sortColumnsByPosition(next) };
    });
  };

  const setCardsByColumnId = (updater) => {
    if (typeof setKanban !== "function") return;
    setKanban((prev) => {
      const prevCols = sortColumnsByPosition(prev?.columns || kanbanColumns || []);
      const prevCards = normalizeCardsByColumn(prevCols, prev?.cards_by_column_id || {});
      const next = typeof updater === "function" ? updater(prevCards) : updater;
      const prevCounts = prev?.column_counts_by_id;
      let nextCounts = prevCounts;

      if (prevCounts) {
        nextCounts = { ...prevCounts };
        Object.keys(next || {}).forEach((colId) => {
          const prevLen = Array.isArray(prevCards[colId]) ? prevCards[colId].length : 0;
          const nextLen = Array.isArray(next[colId]) ? next[colId].length : 0;
          if (prevLen === nextLen) return;
          const current = nextCounts[colId] ?? 0;
          nextCounts[colId] = Math.max(0, current + (nextLen - prevLen));
        });
      }

      return {
        ...(prev || {}),
        columns: prevCols,
        cards_by_column_id: next,
        ...(nextCounts ? { column_counts_by_id: nextCounts } : {}),
      };
    });
  };
  const formatPercent = (value) => {
    if (typeof value !== "number") return "—";
    return intl.formatNumber(value, { style: "percent", maximumFractionDigits: 1 });
  };

  const formatDays = (value) => {
    if (typeof value !== "number") return "—";
    return `${intl.formatNumber(value, { maximumFractionDigits: 1 })}${intl.formatMessage({ id: "recruiter.kanban.days_short" })}`;
  };

  const formatLastUpdated = (value) => {
    if (!value) return "—";

    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";

    return new Intl.DateTimeFormat("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    }).format(d);
  };

  const isFiltering = query.trim().length > 0;

  const applyKanbanMetaUpdate = useCallback(
    (meta) => {
      if (!meta) return;
      if (typeof setKanban !== "function") return;

      setKanban((prev) => {
        const next = { ...(prev || {}) };
        if (meta?.stats) next.stats = meta.stats;
        if (meta?.column_stats_by_id) next.column_stats_by_id = meta.column_stats_by_id;
        return next;
      });
    },
    [setKanban]
  );

  const notifyMetricsInvalidated = useCallback(() => {
    if (typeof onMetricsInvalidate === "function") {
      onMetricsInvalidate();
    }
  }, [onMetricsInvalidate]);

  const filteredCardsByColumnId = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return cardsByColumnId;
    const next = {};
    Object.entries(cardsByColumnId || {}).forEach(([colId, cards]) => {
      next[colId] = (cards || []).filter((c) => {
        const name = (c?.candidate?.full_name || "").toLowerCase();
        const offer = (c?.job_offer?.job_title || "").toLowerCase();
        return name.includes(q) || offer.includes(q);
      });
    });
    return next;
  }, [cardsByColumnId, query]);

  const refreshKanbanCards = useCallback(() => {
    if (loadingAllCards) return;
    setLoadingAllCards(true);

    sh.get("/recruiter/kanban_cards", { params: { limit: 0 } })
      .then((res) => {
        const payload = res?.data?.data || {};
        const nextCards = payload?.cards_by_column_id || {};
        const nextCounts = payload?.column_counts_by_id;

        setKanban((prev) => ({
          ...(prev || {}),
          cards_by_column_id: nextCards,
          ...(nextCounts ? { column_counts_by_id: nextCounts } : {}),
        }));
        setAllCardsLoaded(true);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setLoadingAllCards(false);
      });
  }, [loadingAllCards, setKanban]);

  const loadAllCards = useCallback(() => {
    if (allCardsLoaded) return;
    refreshKanbanCards();
  }, [allCardsLoaded, refreshKanbanCards]);

  const onApplicationReopened = useCallback((reopenedCard) => {
    if (!reopenedCard) return;

    setKanban((prev) => {
      const closedApps = Array.isArray(prev?.closed_applications) ? prev.closed_applications : [];
      const nextClosed = closedApps.filter((app) => app.id !== reopenedCard.id);
      return { ...prev, closed_applications: nextClosed };
    });

    const pendingColumn = columns.find((c) => c.key === "pending") || columns[0];
    if (!pendingColumn) return;

    const pendingColId = String(pendingColumn.id);
    setCardsByColumnId((prev) => {
      const currentCards = prev[pendingColId] || [];
      return {
        ...prev,
        [pendingColId]: [{ ...reopenedCard, status: "pending", last_moved_at: new Date().toISOString() }, ...currentCards],
      };
    });
  }, [columns, setKanban, setCardsByColumnId]);

  const onApplicationClosed = useCallback(
    (closedCard) => {
      if (!closedCard) return;
      if (typeof setKanban !== "function") return;

      setKanban((prev) => {
        const prevClosed = Array.isArray(prev?.closed_applications) ? prev.closed_applications : [];
        const nextClosed = [closedCard, ...prevClosed.filter((app) => app?.id !== closedCard?.id)];
        return { ...(prev || {}), closed_applications: nextClosed };
      });
    },
    [setKanban]
  );

  const columnStatsById = kanban?.column_stats_by_id || {};

  return (
    <Box sx={{ width: "100%", my: "40px" }}>
      <BackdropScreen open={loading} />
      {!loading && (
        <>
      <HeaderRowKanban
        stepsCount={`${stepsCount} ${stepsCount > 1 ? intl.formatMessage({ id: "recruiter.kanban.steps" }) : intl.formatMessage({ id: "recruiter.kanban.step" })}`}
        query={query}
        onQueryChange={setQuery}
        onAddCandidate={refreshKanbanCards}
        columns={columns}
        cardsByColumnId={cardsByColumnId}
        setColumns={setColumns}
        setCardsByColumnId={setCardsByColumnId}
        onBoardMutated={notifyMetricsInvalidated}
      />
      <Grid container spacing={1.5} sx={{ mt: isDesktop ? "32px" : "24px" }}>
        <Grid item xs={6} md={2}>
          <StatsCardKanban label={<FormattedMessage id="recruiter.kanban.total_candidates_in_kanban" />} value={totalCandidatesInKanban} />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatsCardKanban
            label={<FormattedMessage id="recruiter.kanban.new_this_week" defaultMessage="New candidates this week" />}
            value={newApplicantApplicationsThisWeek}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatsCardKanban label={<FormattedMessage id="recruiter.kanban.conversion_rate_recruited" />} value={formatPercent(conversionRateRecruited)} />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatsCardKanban
            label={<FormattedMessage id="recruiter.kanban.median_time_in_pending" defaultMessage="Median time in pending" />}
            value={formatDays(medianTimeInPendingDays)}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatsCardKanban label={<FormattedMessage id="recruiter.kanban.waiting_over_7_days" />} value={waitingOver7Days} />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatsCardKanban label={<FormattedMessage id="recruiter.kanban.last_updated_at" />} value={formatLastUpdated(lastUpdatedAt)} />
        </Grid>
      </Grid>

      <KanbanBoard
        columns={columns}
        setColumns={setColumns}
        columnStatsById={columnStatsById}
        columnCountsById={columnCountsById}
        cardsByColumnId={filteredCardsByColumnId}
        setCardsByColumnId={setCardsByColumnId}
        dragDisabled={isFiltering}
        onKanbanMetaUpdate={applyKanbanMetaUpdate}
        onApplicationClosed={onApplicationClosed}
        onLoadAllCards={loadAllCards}
        loadingAllCards={loadingAllCards}
        allCardsLoaded={allCardsLoaded}
        onBoardMutated={notifyMetricsInvalidated}
      />

      <Grid container spacing={1.5} sx={{ mt: isDesktop ? "24px" : "16px" }}>
        <Grid item xs={6} md={2}>
          <StatsCardKanban
            label={<FormattedMessage id="recruiter.kanban.recruitments_current_month" defaultMessage="Recruitements (mois courant)" />}
            value={recruitmentsCurrentMonth}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatsCardKanban
            label={<FormattedMessage id="recruiter.kanban.closures_current_month" defaultMessage="Clôtures (mois courant)" />}
            value={closuresCurrentMonth}
          />
        </Grid>
        <Grid item xs={6} md={2}>
          <StatsCardKanban
            label={<FormattedMessage id="recruiter.kanban.avg_time_to_hire" defaultMessage="Avg. time to hire" />}
            value={formatDays(avgTimePendingToHiredDays)}
          />
        </Grid>
      </Grid>

      <ClosedCandidatesSection
        applications={kanban?.closed_applications || []}
        query={query}
        onApplicationReopened={onApplicationReopened}
        onKanbanMetaUpdate={applyKanbanMetaUpdate}
      />
        </>
      )}
    </Box>
  );
};

export default CandidatesContent;
